import { type JwtPayload, jwtDecode } from "jwt-decode";
import {
  type EnumPersonRole,
  useGetLicenseStatusAndPersonInfoForAuthQuery,
  useGetPersonInfoQuery,
  useWhoAmIQuery,
} from "~/generated/graphql";
import config from "../../../config";
import { isTest } from "./IsTest";
import isDev from "./isDev";

export interface JwtInterface extends JwtPayload {
  editableContestantIDs: string[];
  role: EnumPersonRole;
  team: string;
}

// TODO refactor these functions into a single getJWTData func??
export function checkLoggedIn() {
  const jwt = config.get("security.jwt") as string;
  if (!jwt) return false;
  const decoded = jwtDecode<JwtInterface>(jwt);
  if (decoded != null && decoded.exp != null) {
    const now = Date.now();
    const exp = new Date(decoded.exp * 1000).getTime();
    return now <= exp;
  }
  return false;
}

export function getRole(): EnumPersonRole | undefined {
  const jwt = config.get("security.jwt") as string;
  if (!jwt) return undefined;
  const decoded = jwtDecode<JwtInterface>(jwt);
  return decoded.role ?? undefined;
}

export function getTeamID(): string | undefined {
  const jwt = config.get("security.jwt") as string;
  if (!jwt) return undefined;
  const decoded = jwtDecode<JwtInterface>(jwt);
  return decoded?.team ?? undefined;
}

export function getUserID(): string | undefined {
  const jwt = config.get("security.jwt") as string;
  if (!jwt) return undefined;
  const decoded = jwtDecode<JwtInterface>(jwt);
  return decoded?.sub ?? undefined;
}
export function getUserEditableContestantIDs(): string[] | undefined {
  return config.get("security.editableContestantIDs") ?? undefined;
}

export function checkifIsProUser() {
  const isDevEnv = isDev();
  const isTestEnv = isTest;

  const userID = getUserID();

  // return license check and enabled features for a user
  const { data, isLoading } = useGetLicenseStatusAndPersonInfoForAuthQuery(
    {
      personID: userID,
    },
    {
      enabled: !isDevEnv && !isTestEnv,
    },
  );

  // skip the license restrictions checks during testing and development
  if (isDevEnv || isTestEnv) {
    console.log("proUser has been set to TRUE for development and testing");
    return { isProUser: true, restrictions: {}, isLoading: false };
  }

  // restrict everything during loading
  if (isLoading || !data) {
    return {
      isProUser: false,
      restrictions: {
        roleRestrictions: {
          isTimeTracker: false,
          isTimeTrackerAdmin: false,
          hideKPIs: true,
        },
        featureLicenses: undefined,
      },
      isLoading,
    };
  }

  // hideKPIs might be null, in which case they might be shown when we don't want it so this is a fallback
  const person = data.person
    ? {
        ...data.person,
        hideKPIs: data.person.hideKPIs ?? true,
      }
    : {
        isTimeTracker: false,
        isTimeTrackerAdmin: false,
        hideKPIs: true,
      };

  // if user has a valid license and isn't using 'light' version then set proUser to true
  const isProUser = Boolean(data.license?.valid && !data.license?.features?.lightVersion);

  return {
    isProUser,
    restrictions: {
      roleRestrictions: person,
      featureLicenses: data.license?.features,
    },
    isLoading: false,
  };
}

export function hasRequiredRoles(restrictedToRoles?: EnumPersonRole[]) {
  if (restrictedToRoles === undefined || restrictedToRoles === null) return true;

  const myRole = getRole();

  if (!myRole) return false;

  return restrictedToRoles.some(x => x === myRole);
}

export function isTimeTrackerAdmin() {
  const meQuery = useWhoAmIQuery();
  const person = useGetPersonInfoQuery({
    id: meQuery.data?.whoami.claims.sub,
  });
  return person.data?.person?.isTimeTrackerAdmin || false;
}

export function hasRequiredTeam(team: string | null | undefined, emptyResponse: boolean = true) {
  if (!team) return emptyResponse;

  const myTeam = getTeamID();

  return myTeam === team;
}
